import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Save, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["caps", "per_user_daily_usd", "Spend per user per day"],
  ["caps", "global_daily_usd", "Spend across all users per day"],
  ["caps", "max_single_job_usd", "Max cost of a single job"],
  ["live_spend", "per_run_cap_usd", "Live video — max per run"],
  ["ai", "max_daily_usd", "OpenRouter AI per day"],
  ["ai", "max_request_usd", "OpenRouter AI per request"],
  ["ai", "max_presenter_request_usd", "Talking presenter per video"],
  ["pricing", "markup_pct", "Default markup, admin use (%)"],
  ["pricing", "credit_usd", "Value of 1 credit"],
];

const PLANS = ["Starter", "Growth", "Scale", "Enterprise"];

const toForm = (cfg) => {
  const f = {};
  PLANS.forEach(pl => { f[`mbp.${pl}`] = cfg.pricing?.markup_by_plan?.[pl] ?? ""; });
  FIELDS.forEach(([s, k]) => { f[`${s}.${k}`] = cfg[s]?.[k] ?? ""; });
  f.unlimited = cfg.live_spend?.cap_usd == null;
  f.total = cfg.live_spend?.cap_usd ?? "";
  return f;
};

export const SpendLimitsEditor = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["studio-overview"], queryFn: () => api.get("/admin/creative/overview").then(r => r.data) });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setForm(toForm(data.config)); }, [data]);
  if (!form) return null;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    const patch = { caps: {}, ai: {}, live_spend: {}, pricing: {} };
    for (const [s, k, label] of FIELDS) {
      const n = Number(form[`${s}.${k}`]);
      if (form[`${s}.${k}`] === "" || Number.isNaN(n) || n < 0) return toast.error(`${label}: enter a number of 0 or more`);
      if (k === "credit_usd" && n <= 0) return toast.error(`${label}: must be more than 0`);
      patch[s][k] = n;
    }
    patch.pricing.markup_by_plan = {};
    for (const pl of PLANS) {
      const n = Number(form[`mbp.${pl}`]);
      if (form[`mbp.${pl}`] === "" || Number.isNaN(n) || n < 0) return toast.error(`${pl} markup: enter a number of 0 or more`);
      patch.pricing.markup_by_plan[pl] = n;
    }
    if (!form.unlimited) {
      const t = Number(form.total);
      if (form.total === "" || Number.isNaN(t) || t < 0) return toast.error("Live video total: enter a number of 0 or more");
      patch.live_spend.cap_usd = t;
    } else patch.live_spend.cap_usd = null;
    setSaving(true);
    try {
      const { data: cfg } = await api.patch("/admin/creative/config", patch);
      setForm(toForm(cfg));
      qc.invalidateQueries({ queryKey: ["studio-usage"] });
      qc.invalidateQueries({ queryKey: ["studio-overview"] });
      qc.invalidateQueries({ queryKey: ["studio-pricing"] });
      toast.success("Spend limits saved");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg : (d?.message || d || "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <article className="panel full-span" data-testid="spend-limits-editor">
      <span className="eyebrow"><SlidersHorizontal size={11} /> LIMIT EDITOR</span><h2>Spend limits &amp; pricing</h2>
      <p className="studio-muted">Users pay the provider cost plus your markup. Credits are shown next to every USD price.</p>
      <div className="limits-grid">
        {FIELDS.map(([s, k, label]) => (
          <label key={k} className="limit-field">
            <small>{label}{k === "markup_pct" ? "" : " (USD)"}</small>
            <input type="number" disabled={k === "credit_usd"} min={0} step="0.01" value={form[`${s}.${k}`]} onChange={e => set(`${s}.${k}`, e.target.value)} data-testid={`limit-input-${k.replaceAll("_", "-")}`} />
          </label>
        ))}
        <label className="limit-field">
          <small className="limit-row">Live video — total all time (USD)
            <span className="studio-check"><input type="checkbox" checked={form.unlimited} onChange={e => set("unlimited", e.target.checked)} data-testid="limit-live-total-unlimited" /> Unlimited</span>
          </small>
          <input type="number" min={0} step="0.01" disabled={form.unlimited} placeholder={form.unlimited ? "Unlimited" : ""} value={form.unlimited ? "" : form.total} onChange={e => set("total", e.target.value)} data-testid="limit-input-live-total" />
        </label>
      </div>
      <h3 className="limits-subhead">Markup by plan (%)</h3>
      <div className="limits-grid">
        {PLANS.map(pl => (
          <label key={pl} className="limit-field">
            <small>{pl}</small>
            <input type="number" min={0} step="1" value={form[`mbp.${pl}`]} onChange={e => set(`mbp.${pl}`, e.target.value)} data-testid={`limit-input-markup-${pl.toLowerCase()}`} />
          </label>
        ))}
      </div>
      <button className="primary-btn" style={{ marginTop: 18 }} disabled={saving} onClick={save} data-testid="limits-save-button"><Save size={16} />{saving ? "Saving…" : "Save limits"}</button>
    </article>
  );
};
